import { TooltipProvider } from '@hostingportal/ui'
import { RouterProvider, createRouter } from '@tanstack/react-router'
import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'

import { bootRuntime } from './lib/boot'
import { queryClient } from './lib/query-client'
import { routeTree } from './routeTree.gen'

import './styles/index.css'

// Boot the mock (or live) runtime before the first render.
bootRuntime()

// Stale-deploy recovery: a long-lived tab holds the previous build's chunk
// hashes; after a redeploy the next route-level dynamic import 404s and the
// router updates the URL without ever mounting the new page. Reload once to
// pick up the fresh index.html (session-guarded so a truly broken asset can
// not reload-loop).
window.addEventListener('vite:preloadError', (event) => {
  const KEY = 'hostingportal.chunk-reload-at'
  const last = Number(window.sessionStorage.getItem(KEY) ?? 0)
  if (Date.now() - last < 30_000) return // already tried; surface the error
  event.preventDefault()
  window.sessionStorage.setItem(KEY, String(Date.now()))
  window.location.reload()
})

const router = createRouter({
  routeTree,
  context: { queryClient },
  defaultPreload: 'intent',
  defaultPendingMinMs: 150,
})

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}

const rootEl = document.getElementById('root')
if (!rootEl) throw new Error('No #root element')

createRoot(rootEl).render(
  <StrictMode>
    <TooltipProvider>
      <RouterProvider router={router} />
    </TooltipProvider>
  </StrictMode>,
)
